import { X } from 'lucide-react';

import { PlanSelect } from '@/design-system/primitives/PlanSelect';
import { memberPriceCents, useMembership } from '@/lib/membership';
import { stackGroupCompareCents } from '../stack';
import {
  cart,
  FREQUENCY_CYCLE_LABELS,
  FREQUENCY_LABELS,
  offerableFrequencies,
  type CartItem,
  type PlanFrequency,
} from '../stores/cart';

function formatPrice(cents: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: cents % 100 ? 2 : 0,
  }).format(cents / 100);
}

/** A stack in the cart: one row, one price, one plan length for both lines.
 *  Remove stack takes both out; removing one line leaves the other at its
 *  single price (the cart store reprices it). A pay-link recovery shows the
 *  same way with just the product it finishes. */
export function StackCartGroup({
  stackId,
  lines,
  locked,
}: {
  stackId: string;
  lines: CartItem[];
  locked: boolean;
}) {
  const { percent: memberPercent } = useMembership();
  const term = lines[0].frequency ?? 'monthly';
  const total = lines.reduce((sum, line) => sum + line.price_cents, 0);
  const compare = stackGroupCompareCents(lines);
  // Members pay 10% less on each line, as the server charges.
  const memberCents = (pick: (line: CartItem) => number) =>
    lines.reduce(
      (sum, line) => sum + memberPriceCents(pick(line), memberPercent),
      0,
    );
  const firstCharge = memberCents((line) => line.price_cents);
  const renewal = memberCents(
    (line) => line.renewal_prices?.[term] ?? line.price_cents,
  );
  // Terms every line is priced at; the stack moves between them together.
  const terms = offerableFrequencies(lines[0]).filter((f) =>
    lines.every((line) => offerableFrequencies(line).includes(f)),
  );
  const termTotal = (f: PlanFrequency) =>
    lines.reduce((sum, line) => sum + (line.plan_prices?.[f] ?? 0), 0);
  const firstLabel =
    term === 'monthly'
      ? 'first month'
      : `first ${FREQUENCY_LABELS[term].toLowerCase()} supply`;
  const title = lines[0].stack_title ?? lines.map((l) => l.title).join(' + ');
  // A pay link finishing a stack whose other product is already paid.
  const recovery = Boolean(lines[0].stack_recovery);

  return (
    <li
      className="flex flex-col border-b border-line-subtle"
      aria-label={title}
      data-stack-id={stackId}
    >
      <div className="flex items-start justify-between gap-15 px-15 pt-12">
        <div className="min-w-0 flex items-center gap-8">
          <img
            src="/icons/icon-stack.svg"
            alt=""
            aria-hidden
            className="w-12 h-12 object-contain shrink-0"
          />
          <div className="type-title-sm text-ink truncate uppercase">
            {title}
          </div>
        </div>
        <div className="shrink-0 flex items-baseline gap-6">
          {compare > total && (
            <s className="type-body-sm text-ink-muted">
              <span className="sr-only">was </span>
              {formatPrice(compare)}
            </s>
          )}
          <span className="type-data-md text-ink">{formatPrice(total)}</span>
        </div>
      </div>
      <ul className="flex flex-col gap-8 px-15 pt-10">
        {lines.map((line) => (
          <li key={line.id} className="flex items-center gap-10">
            <div className="w-40 h-40 shrink-0 bg-[var(--surface-sunken)] overflow-hidden">
              {line.image_url && (
                <img
                  src={line.image_url}
                  alt=""
                  aria-hidden
                  className="w-full h-full object-cover"
                />
              )}
            </div>
            <div className="min-w-0 flex-1">
              <div className="type-label-sm text-ink truncate">{line.title}</div>
              {line.subtitle && (
                <div className="type-body-sm text-ink-secondary truncate">
                  {line.subtitle}
                </div>
              )}
            </div>
            {!recovery && (
              // The other line stays in the cart at its own single price.
              <button
                type="button"
                disabled={locked}
                aria-label={`Remove ${line.title}`}
                onClick={() => cart.remove(line.id)}
                className="shrink-0 min-h-44 min-w-44 inline-flex items-center justify-center text-ink-secondary hover:text-ink disabled:opacity-60"
              >
                <X size={14} strokeWidth={2} />
              </button>
            )}
          </li>
        ))}
      </ul>
      <fieldset disabled={locked}>
        {terms.length > 1 && (
          <div className="px-15 pt-12 pb-14">
            <PlanSelect
              aria-label="Stack plan length"
              value={term}
              onChange={(e) =>
                cart.setStackFrequency(stackId, e.target.value as PlanFrequency)
              }
            >
              {terms.map((f) => (
                <option key={f} value={f}>
                  {FREQUENCY_LABELS[f]} | {formatPrice(termTotal(f))}
                </option>
              ))}
            </PlanSelect>
          </div>
        )}
        <p className="px-15 pb-10 type-label-xs text-ink-muted">
          {formatPrice(firstCharge)} {firstLabel} | renews at{' '}
          {formatPrice(renewal)}
          {FREQUENCY_CYCLE_LABELS[term]}
          {memberPercent > 0 ? ' member price' : ''}.{' '}
          {recovery
            ? 'Completes your stack at the stack price.'
            : 'Both treatments share this plan. Remove one and the other goes back to its own price.'}
        </p>
      </fieldset>
      <button
        type="button"
        disabled={locked}
        onClick={() => cart.removeStack(stackId)}
        className="mt-auto flex items-center justify-between px-15 py-8 type-body-sm text-ink-secondary bg-[var(--surface-sunken)] border-t border-line-subtle hover:text-ink"
      >
        {recovery ? 'Remove' : 'Remove stack'}
        <X size={14} strokeWidth={2} />
      </button>
    </li>
  );
}
